import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { MOCK_BOOTHS, CANDIDATE_NAME } from '../constants';
import { getConstituencyOverview } from '../services/geminiService';
import { Activity, Users, Home, TrendingUp, AlertTriangle } from 'lucide-react';

const COLORS = ['#FF9933', '#000080', '#138808', '#FF8042']; // Saffron, Blue, Green

const Dashboard: React.FC = () => {
  const [aiSummary, setAiSummary] = useState<string>("Initializing War Room Intelligence...");
  
  useEffect(() => {
    // Fire and forget AI call for summary
    getConstituencyOverview(MOCK_BOOTHS).then(setAiSummary);
  }, []);

  const totalVoters = MOCK_BOOTHS.reduce((acc, b) => acc + b.totalVoters, 0);
  const totalPannaPramukhs = MOCK_BOOTHS.reduce((acc, b) => acc + b.verifiedPannaPramukhs, 0);
  const targetPannaPramukhs = MOCK_BOOTHS.reduce((acc, b) => acc + b.targetPannaPramukhs, 0);
  const coveragePercent = Math.round((totalPannaPramukhs / targetPannaPramukhs) * 100);

  // Data for Charts
  const trendData = [
    { name: 'Strong BJP', value: MOCK_BOOTHS.filter(b => b.lastElectionTrend === 'BJP').length },
    { name: 'Strong INC', value: MOCK_BOOTHS.filter(b => b.lastElectionTrend === 'INC').length },
    { name: 'Strong AIUDF', value: MOCK_BOOTHS.filter(b => b.lastElectionTrend === 'AIUDF').length },
  ];

  const issueCounts: Record<string, number> = {};
  MOCK_BOOTHS.forEach(b => {
    b.keyIssues.forEach(issue => {
      issueCounts[issue] = (issueCounts[issue] || 0) + 1;
    });
  });
  const issueData = Object.keys(issueCounts).map(k => ({ name: k, count: issueCounts[k] }));

  // --- Text Formatting Helpers ---
  const parseBold = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-bold text-slate-800">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderFormattedText = (text: string) => {
    if (!text) return null;
    if (text.startsWith("Initializing") || text.startsWith("Error")) {
        return <p className="text-slate-500 italic animate-pulse">{text}</p>;
    }

    const lines = text.split('\n');
    return (
        <div className="space-y-3 text-sm text-slate-700 leading-relaxed font-normal">
            {lines.map((line, index) => {
                const trimmed = line.trim();
                if (!trimmed) return <div key={index} className="h-1" />;

                // Headings
                if (trimmed.startsWith('###') || (trimmed.endsWith(':') && trimmed.length < 50)) {
                   return <h4 key={index} className="text-base font-bold text-slate-900 mt-2">{parseBold(trimmed.replace(/^#+\s/, ''))}</h4>;
                }

                // Bullet points
                if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('• ')) {
                    return (
                        <div key={index} className="flex gap-2 ml-2">
                            <span className="text-bjp-saffron mt-1.5 min-w-[6px] h-[6px] rounded-full bg-bjp-saffron block"></span>
                            <div className="flex-1">{parseBold(trimmed.replace(/^[-*•]\s+/, ''))}</div>
                        </div>
                    );
                }

                // Numbered lists
                if (/^\d+\.\s/.test(trimmed)) {
                     const [num, ...rest] = trimmed.split('.');
                     const content = rest.join('.').trim();
                     return (
                        <div key={index} className="flex gap-2 ml-1">
                            <span className="font-bold text-bjp-saffron min-w-[1.2rem]">{num}.</span>
                            <div className="flex-1">{parseBold(content)}</div>
                        </div>
                     );
                }

                return <p key={index}>{parseBold(line)}</p>;
            })}
        </div>
    );
  };

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Mission 2026: Borkhetri</h2>
          <p className="text-sm text-slate-500">Candidate: <span className="font-semibold text-bjp-saffron">{CANDIDATE_NAME}</span></p>
        </div>
        <div className="mt-4 md:mt-0 flex gap-2">
          <span className="px-3 py-1 bg-bjp-saffron/10 text-bjp-saffron text-sm font-medium rounded-full border border-bjp-saffron/20">
            Assam Pradesh BJP
          </span>
          <span className="px-3 py-1 bg-green-100 text-bjp-green text-sm font-medium rounded-full border border-green-200">
            Active War Room
          </span>
        </div>
      </div>

      {/* AI Strategy Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-orange-50/50 px-6 py-4 border-b border-orange-100 flex items-center gap-2">
           <Activity className="w-5 h-5 text-bjp-saffron" />
           <h3 className="font-bold text-slate-800">Strategic Overview <span className="text-slate-400 font-normal text-sm ml-1">(AI Generated)</span></h3>
        </div>
        <div className="p-6">
          {renderFormattedText(aiSummary)}
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Users className="w-4 h-4" /> Total Voters Tracked
          </div>
          <div className="text-2xl font-bold text-slate-800">{totalVoters.toLocaleString()}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Users className="w-4 h-4" /> Panna Pramukh Strength
          </div>
          <div className="text-2xl font-bold text-slate-800">
            {totalPannaPramukhs} <span className="text-sm font-normal text-slate-400">/ {targetPannaPramukhs}</span>
          </div>
          <div className="w-full bg-slate-100 h-1.5 mt-2 rounded-full">
            <div className={`h-1.5 rounded-full ${coveragePercent > 80 ? 'bg-bjp-green' : 'bg-bjp-saffron'}`} style={{ width: `${coveragePercent}%` }}></div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Home className="w-4 h-4" /> Booths Managed
          </div>
          <div className="text-2xl font-bold text-slate-800">{MOCK_BOOTHS.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <AlertTriangle className="w-4 h-4" /> Critical Issue
          </div>
          <div className="text-lg font-semibold text-red-600">River Erosion</div>
          <p className="text-xs text-slate-400">High priority in Char areas</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Booth Inclination */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-500" />
            Historical Booth Inclination
          </h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={trendData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                >
                  {trendData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.name === 'Strong BJP' ? '#FF9933' : entry.name === 'Strong INC' ? '#00BFFF' : '#138808'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Issues */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Dominant Local Issues</h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={issueData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} tick={{fontSize: 12}} />
                <Tooltip />
                <Bar dataKey="count" fill="#FF9933" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;